import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { orgAPI } from "../lib/api";
import { DEFAULT_TERMINOLOGY, getVocabulary } from "../lib/terminology";

/* Hält die Organisation für den eingeloggten Bereich und leitet daraus das Vokabular ab.
   Der Fetch lag vorher in DashboardLayout — er wohnt hier, damit auch die Seiten unter
   <Outlet /> ihn nutzen können, ohne dass ein zweiter Request entsteht. */

const TerminologyContext = createContext(null);

export function TerminologyProvider({ children }) {
  const [org, setOrg] = useState(null);
  const [orgLoaded, setOrgLoaded] = useState(false);

  const loadOrg = useCallback(async (config) => {
    try {
      const res = await orgAPI.get({ params: { compact: true }, ...(config || {}) });
      if (!config?.signal?.aborted) setOrg(res.data || null);
    } catch (error) {
      if (error?.name === "AbortError" || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
      console.warn("Organisation konnte nicht geladen werden:", error);
    } finally {
      if (!config?.signal?.aborted) setOrgLoaded(true);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadOrg({ signal: controller.signal });
    return () => controller.abort();
  }, [loadOrg]);

  useEffect(() => {
    const handleOrgUpdated = () => { void loadOrg({ forceRefresh: true }); };
    window.addEventListener("org-updated", handleOrgUpdated);
    return () => window.removeEventListener("org-updated", handleOrgUpdated);
  }, [loadOrg]);

  const value = useMemo(() => {
    /* Solange nichts geladen ist, gilt "clients" — der heutige Wortlaut. So flackert
       die Oberfläche nicht kurz in die falschen Begriffe. */
    const terminology = orgLoaded ? (org?.terminology || DEFAULT_TERMINOLOGY) : DEFAULT_TERMINOLOGY;
    return {
      org,
      orgLoaded,
      setOrg,
      refreshOrg: loadOrg,
      terminology,
      vocab: getVocabulary(terminology),
    };
  }, [org, orgLoaded, loadOrg]);

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
}

export function useTerminology() {
  const context = useContext(TerminologyContext);
  if (!context) throw new Error("useTerminology muss innerhalb von TerminologyProvider verwendet werden");
  return context;
}

export function useVocab() {
  return useTerminology().vocab;
}

export default TerminologyContext;
